import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter, map, Observable, of, switchMap, take } from 'rxjs';
import type {
	GusAddress,
	GusCompany,
	OrganizationAddressAddDialogData,
} from '@/organization/organization/organization.model';
import { OrganizationService } from '@/organization/organization/organization.service';
import { OrganizationAddressAddComponent } from '@/organization/organization/organization-primary/organization-address-add/organization-address-add.component';
import { OrganizationDataDownloadComponent } from './organization-data-download.component';
import { toGusHeadquartersAddress } from './organization-gus.util';

@Injectable({
	providedIn: 'root',
})
export class OrganizationGusImportService {
	private readonly dialog = inject(MatDialog);
	private readonly organizationService = inject(OrganizationService);

	importCompany(): Observable<GusCompany> {
		return this.dialog
			.open<OrganizationDataDownloadComponent, void, GusCompany>(OrganizationDataDownloadComponent)
			.afterClosed()
			.pipe(
				take(1),
				filter((company): company is GusCompany => !!company),
				switchMap((company) => {
					const data = this.organizationService.organizationData;

					data.nip = company.nip;
					data.regon = company.regon;
					data.name = company.name;

					return this.applyHeadquarters(company.address).pipe(map(() => company));
				}),
			);
	}

	private applyHeadquarters(address: GusAddress | null): Observable<unknown> {
		if (!address) {
			return of(null);
		}

		const draft = toGusHeadquartersAddress(address, this.organizationService.enterpriseData.locations);

		if (draft.location) {
			this.organizationService.organizationData.address.push(draft);
			return of(null);
		}

		return this.dialog
			.open<OrganizationAddressAddComponent, OrganizationAddressAddDialogData, boolean>(
				OrganizationAddressAddComponent,
				{ data: { address: draft, city: address.city } },
			)
			.afterClosed()
			.pipe(take(1));
	}
}
